// components/holidays/ApproveTourPlanModal.tsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toast } from '../../utils/toast';
import { fmtDateWithDay, fmtMonth } from '../../utils/datetime';
import {
  X, Eye, Check, XCircle, Search, Filter, ChevronRight,
  Users, Calendar, ArrowLeft, CheckCircle, XCircle as XCircleFull,
  Download, MoreVertical, User, MapPin, Building, AlertCircle, FileText
} from 'lucide-react';
import { useApprovalFlow } from '../../hooks/useApprovalFlow';
import { useAuth } from '../../context/AuthContext';

interface ApproveTourPlanModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;

  onViewTourPlan?: (plan: ReturnType<typeof useApprovalFlow>["tourPlans"][0]) => void;
  onApproveTourPlan?: (plan: ReturnType<typeof useApprovalFlow>["tourPlans"][0]) => void;
  onRejectTourPlan?: (plan: ReturnType<typeof useApprovalFlow>["tourPlans"][0]) => void;
  onViewLeave?: (leave: ReturnType<typeof useApprovalFlow>["leaves"][0]) => void;
  onApproveLeave?: (leave: ReturnType<typeof useApprovalFlow>["leaves"][0]) => void;
  onRejectLeave?: (leave: ReturnType<typeof useApprovalFlow>["leaves"][0]) => void;
}

type ActiveTab = 'tour-plans' | 'leaves';
type Status = 'all' | 'pending' | 'approved' | 'rejected';

const ApproveTourPlanModal: React.FC<ApproveTourPlanModalProps> = ({
  isOpen,
  onOpenChange,
  onViewTourPlan,
  onApproveTourPlan,
  onRejectTourPlan,
  onViewLeave,
  onApproveLeave,
  onRejectLeave
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('tour-plans');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [filters, setFilters] = useState({
    month: '',
    year: '',
    employee: '',
    status: 'all' as Status,
    region: '',
    designation: ''
  });
  const [selectedEmployee, setSelectedEmployee] = useState<ReturnType<typeof useApprovalFlow>["users"][0] | null>(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [showBulkActionModal, setShowBulkActionModal] = useState<{
    visible: boolean;
    actionType: 'approve' | 'reject';
  }>({
    visible: false,
    actionType: 'approve'
  });

  const { user } = useAuth();


  // Use the actual hook
  const {
    users,
    tourPlans,
    leaves,
    loading,
    fetchApprovalEmployeesList,
    fetchEmployeeTourPlans,
    fetchEmployeeLeaves,
  } = useApprovalFlow();

  // Fetch employees on mount when modal opens
  useEffect(() => {
    if (isOpen && user && user.tenant_id) {
      fetchApprovalEmployeesList(user.tenant_id);
    }
  }, [isOpen, user, fetchApprovalEmployeesList]);

  // Fetch employee details when selected
  useEffect(() => {
    if (selectedEmployee && user.tenant_id) {
      if (activeTab === 'tour-plans') {
        fetchEmployeeTourPlans(user.tenant_id, selectedEmployee.employee_id);
      } else {
        fetchEmployeeLeaves(user.tenant_id, selectedEmployee.employee_id);
      }
    }
  }, [selectedEmployee, activeTab, user, fetchEmployeeTourPlans, fetchEmployeeLeaves]);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = ['2024', '2025', '2026'];

  // Extract unique regions and designations from users
  const regions = useMemo(() => {
    const uniqueRegions = new Set<string>();
    users.forEach(user => {
      if (user.region) uniqueRegions.add(user.region);
    });
    return Array.from(uniqueRegions);
  }, [users]);

  const designations = ['MR', 'ASM', 'ZSM', 'RSM'];

  // Get color based on designation
  const getDesignationColor = (designation: string): string => {
    switch (designation) {
      case 'MR': return 'bg-blue-100 text-blue-800';
      case 'ASM': return 'bg-green-100 text-green-800';
      case 'ZSM': return 'bg-purple-100 text-purple-800';
      case 'RSM': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get color based on status
  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'pending':
      case 'submitted':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'pending_deviation':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'approved':
      case 'approved_deviation':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'rejected':
      case 'rejected_deviation':
        return 'bg-red-100 text-red-800 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(employee =>
      employee.name.toLowerCase().includes(query) ||
      employee.designation.toLowerCase().includes(query) ||
      (employee.region && employee.region.toLowerCase().includes(query))
    );
  }, [searchQuery, users]);

  // Filter and sort tour plans
  const filteredTourPlans = useMemo(() => {
    let filtered = tourPlans;

    if (filters.status !== "all") {
      filtered = filtered.filter((plan) => {
        if (filters.status === "pending") return plan.status === "pending" || plan.status === "submitted";
        return plan.status.toLowerCase() === filters.status;
      });
    }

    if (filters.month) {
      filtered = filtered.filter((plan) => fmtMonth(plan.date) === filters.month);
    }

    if (filters.year) {
      filtered = filtered.filter((plan) => {
        const planDate = new Date(plan.date);
        return planDate.getFullYear().toString() === filters.year;
      });
    }

    // ✅ sort a copy (never mutate state arrays)
    return [...filtered].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [tourPlans, filters]);

  // Filter and sort leaves
  const filteredLeaves = useMemo(() => {
    let filtered = leaves;

    if (filters.status !== "all") {
      filtered = filtered.filter((leave) => {
        if (filters.status === "pending") return leave.status === "pending" || leave.status === "submitted";
        return leave.status.toLowerCase() === filters.status;
      });
    }

    if (filters.month) {
      filtered = filtered.filter((leave) => fmtMonth(leave.date) === filters.month);
    }

    if (filters.year) {
      filtered = filtered.filter((leave) => {
        const leaveDate = new Date(leave.date);
        return leaveDate.getFullYear().toString() === filters.year;
      });
    }

    // ✅ sort a copy
    return [...filtered].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [leaves, filters]);


  // Clear all filters
  const clearFilters = () => {
    setFilters({
      month: '',
      year: '',
      employee: '',
      status: 'all',
      region: '',
      designation: ''
    });
    setSelectedItems([]);
    setIsSelectMode(false);
  };

  // Handle employee selection
  const handleEmployeeSelect = (employee: ReturnType<typeof useApprovalFlow>["users"][0]) => {
    setSelectedEmployee(employee);
    clearFilters(); // Clear filters when selecting employee
  };

  // Handle back to employee list
  const handleBackToList = () => {
    setSelectedEmployee(null);
    clearFilters();
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('deviation')) {
      return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Handle single item actions
  const handleApproveTourPlan = async (plan: ReturnType<typeof useApprovalFlow>["tourPlans"][0]) => {
    if (await toast.confirm('Approve this tour plan?', 'Confirm Approval', 'Approve')) {
      onApproveTourPlan?.(plan);
    }
  };

  const handleRejectTourPlan = async (plan: ReturnType<typeof useApprovalFlow>["tourPlans"][0]) => {
    if (await toast.confirm('Reject this tour plan?', 'Confirm Rejection', 'Reject')) {
      onRejectTourPlan?.(plan);
    }
  };

  const handleApproveLeave = async (leave: ReturnType<typeof useApprovalFlow>["leaves"][0]) => {
    if (await toast.confirm('Approve this leave?', 'Confirm Approval', 'Approve')) {
      onApproveLeave?.(leave);
    }
  };

  const handleRejectLeave = async (leave: ReturnType<typeof useApprovalFlow>["leaves"][0]) => {
    if (await toast.confirm('Reject this leave?', 'Confirm Rejection', 'Reject')) {
      onRejectLeave?.(leave);
    }
  };

  // Handle bulk actions
  const handleBulkAction = (action: 'approve' | 'reject') => {
    if (selectedItems.length === 0) {
      toast.warning('Please select at least one item to perform bulk action.');
      return;
    }
    setShowBulkActionModal({ visible: true, actionType: action });
  };

  const executeBulkAction = () => {
    const { actionType } = showBulkActionModal;
    if (activeTab === 'tour-plans') {
      selectedItems.forEach(id => {
        const plan = tourPlans.find(p => p.id === id);
        if (plan) {
          if (actionType === 'approve') {
            onApproveTourPlan?.(plan);
          } else {
            onRejectTourPlan?.(plan);
          }
        }
      });
    } else {
      selectedItems.forEach(id => {
        const leave = leaves.find(l => l.id === id);
        if (leave) {
          if (actionType === 'approve') {
            onApproveLeave?.(leave);
          } else {
            onRejectLeave?.(leave);
          }
        }
      });
    }
    setSelectedItems([]);
    setIsSelectMode(false);
    setShowBulkActionModal({ visible: false, actionType: 'approve' });
  };

  // Select all items
  const selectAllItems = () => {
    const items = activeTab === 'tour-plans' ? filteredTourPlans : filteredLeaves;
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  // Handle individual item selection
  const handleItemSelect = (id: number, selected: boolean) => {
    if (selected) {
      setSelectedItems(prev => [...prev, id]);
    } else {
      setSelectedItems(prev => prev.filter(itemId => itemId !== id));
    }
  };

  // Toggle select mode
  const toggleSelectMode = () => {
    if (isSelectMode) {
      setSelectedItems([]);
    }
    setIsSelectMode(!isSelectMode);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            {selectedEmployee ? (
              <button
                onClick={handleBackToList}
                className="text-gray-600 hover:text-gray-900 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                aria-label="Back to list"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (
              <Users className="w-6 h-6 text-blue-600" />
            )}
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                {selectedEmployee
                  ? `${selectedEmployee.name}'s ${activeTab === 'tour-plans' ? 'Tour Plans' : 'Leaves'}`
                  : 'Approval Dashboard'
                }
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {selectedEmployee
                  ? `Review and manage ${activeTab === 'tour-plans' ? 'tour plan approvals' : 'leave approvals'}`
                  : 'Manage tour plan and leave approvals for your team'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!selectedEmployee && (
              <>
                {showSearch ? (
                  <div className="flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2 w-64">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="ml-2 flex-1 outline-none text-sm"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setShowSearch(false)}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSearch(true)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => onOpenChange(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Employee List */}
          {!selectedEmployee && (
            <div className="w-80 border-r border-gray-200 overflow-auto">
              <div className="p-4">
                {/* Tabs */}
                <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
                  <button
                    onClick={() => setActiveTab('tour-plans')}
                    className={`flex-1 py-2 rounded-md text-center font-medium transition-colors ${activeTab === 'tour-plans' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Tour Plans
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('leaves')}
                    className={`flex-1 py-2 rounded-md text-center font-medium transition-colors ${activeTab === 'leaves' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Users className="w-4 h-4" />
                      Leaves
                    </div>
                  </button>
                </div>

                {/* Search Results Header */}
                {searchQuery && (
                  <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Search results for "{searchQuery}"
                    </h3>
                    <p className="text-sm text-gray-600">
                      Found {filteredEmployees.length} user{filteredEmployees.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}

                {/* Employee List */}
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading users...</p>
                  </div>
                ) : filteredEmployees.length > 0 ? (
                  <div className="space-y-3">
                    {filteredEmployees.map((employee) => {
                      const getCountByStatus = (status: string) => {
                        if (activeTab === 'tour-plans') {
                          switch (status) {
                            case 'pending': return employee.pendingTourPlans;
                            case 'approved': return employee.approvedTourPlans;
                            case 'rejected': return employee.rejectedTourPlans;
                            case 'pending_deviation': return employee.pendingDeviation || 0;
                            case 'approved_deviation': return employee.approvedDeviation || 0;
                            case 'rejected_deviation': return employee.rejectedDeviation || 0;
                            default: return 0;
                          }
                        } else {
                          switch (status) {
                            case 'pending': return employee.pendingLeaves;
                            case 'approved': return employee.approvedLeaves;
                            case 'rejected': return employee.rejectedLeaves;
                            default: return 0;
                          }
                        }
                      };

                      const STATUS_LABELS: Record<string, string> = {
                        pending: 'Pending',
                        approved: 'Approved',
                        rejected: 'Rejected',
                        pending_deviation: 'Pending Deviation',
                        approved_deviation: 'Approved Deviation',
                        rejected_deviation: 'Rejected Deviation',
                      };

                      return (
                        <button
                          key={employee.id}
                          onClick={() => handleEmployeeSelect(employee)}
                          className="w-full bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-blue-300 hover:shadow-sm transition-all hover:bg-blue-50"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-700 font-bold">
                                  {employee.avatar}
                                </span>
                              </div>
                              <div className="text-left">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-gray-900">
                                    {employee.name}
                                  </span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDesignationColor(employee.designation)}`}>
                                    {employee.designation}
                                  </span>
                                </div>
                                {employee.region && (
                                  <div className="flex items-center text-sm text-gray-600 mb-2">
                                    <MapPin className="w-3 h-3 mr-1" />
                                    {employee.region}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1">
                                  {Object.keys(STATUS_LABELS).map((status) => {
                                    const count = getCountByStatus(status);
                                    if (count > 0) {
                                      return (
                                        <span
                                          key={status}
                                          className={`px-2 py-1 rounded-full text-xs ${getStatusColor(status)}`}
                                        >
                                          {count} {STATUS_LABELS[status]}
                                        </span>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">
                      {searchQuery ? 'No users found' : 'No items found'}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {searchQuery
                        ? 'Try a different search term'
                        : activeTab === 'tour-plans'
                          ? 'No users have tour plans'
                          : 'No users have leaves'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 overflow-auto">
            <div className="p-6">
              {/* User Info Header (when employee is selected) */}
              {selectedEmployee && (
                <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-700 font-bold text-lg">
                          {selectedEmployee.avatar}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {selectedEmployee.name}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDesignationColor(selectedEmployee.designation)}`}>
                            {selectedEmployee.designation}
                          </span>
                        </div>
                        {selectedEmployee.region && (
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {selectedEmployee.region}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {(filteredTourPlans.length > 0 || filteredLeaves.length > 0) && !isSelectMode && (
                        <button
                          onClick={toggleSelectMode}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Select multiple"
                        >
                          <Filter className="w-5 h-5" />
                        </button>
                      )}
                      {isSelectMode && (
                        <>
                          <button
                            onClick={() => handleBulkAction('reject')}
                            disabled={selectedItems.length === 0}
                            className={`p-2 rounded-lg ${selectedItems.length > 0 ? 'text-red-600 hover:bg-red-50' : 'text-gray-400'}`}
                            title="Reject selected"
                          >
                            <XCircleFull className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleBulkAction('approve')}
                            disabled={selectedItems.length === 0}
                            className={`p-2 rounded-lg ${selectedItems.length > 0 ? 'text-green-600 hover:bg-green-50' : 'text-gray-400'}`}
                            title="Approve selected"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button
                            onClick={toggleSelectMode}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Cancel selection"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Selection Header */}
                  {isSelectMode && (
                    <div className="bg-blue-50 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={selectAllItems}
                          className="flex items-center gap-2 text-blue-700 font-medium hover:text-blue-800"
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedItems.length === (activeTab === 'tour-plans' ? filteredTourPlans.length : filteredLeaves.length) ? 'bg-blue-600 border-blue-600' : 'border-gray-400'}`}>
                            {selectedItems.length === (activeTab === 'tour-plans' ? filteredTourPlans.length : filteredLeaves.length) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          {selectedItems.length === (activeTab === 'tour-plans' ? filteredTourPlans.length : filteredLeaves.length) ? 'Deselect All' : 'Select All'}
                        </button>
                        <span className="text-blue-700 font-semibold">
                          {selectedItems.length} selected
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Filters */}
              <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
                  <select
                    value={filters.month}
                    onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">All Months</option>
                    {months.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>

                  <select
                    value={filters.year}
                    onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">All Years</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>

                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value as Status })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>

                  {!selectedEmployee && (
                    <>
                      <select
                        value={filters.region}
                        onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="">All Regions</option>
                        {regions.map(region => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>

                      <select
                        value={filters.designation}
                        onChange={(e) => setFilters({ ...filters, designation: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="">All Designations</option>
                        {designations.map(desig => (
                          <option key={desig} value={desig}>{desig}</option>
                        ))}
                      </select>
                    </>
                  )}

                  <input
                    type="text"
                    placeholder="Search employee..."
                    value={filters.employee}
                    onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex gap-2">
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Clear Filters
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    Showing {activeTab === 'tour-plans' ? filteredTourPlans.length : filteredLeaves.length} entries
                  </p>
                </div>
              </div>

              {/* Content Table */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading {activeTab === 'tour-plans' ? 'tour plans' : 'leaves'}...</p>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {isSelectMode && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedItems.length === (activeTab === 'tour-plans' ? filteredTourPlans.length : filteredLeaves.length)}
                                onChange={selectAllItems}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </div>
                          </th>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        {!selectedEmployee && activeTab === 'tour-plans' && filteredTourPlans.length > 0 && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                        )}
                        {!selectedEmployee && activeTab === 'leaves' && filteredLeaves.length > 0 && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                        )}
                        {activeTab === 'tour-plans' ? (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deviation</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved By</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          </>
                        ) : (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {activeTab === 'tour-plans' ? (
                        filteredTourPlans.length === 0 ? (
                          <tr>
                            <td colSpan={isSelectMode ? (selectedEmployee ? 7 : 8) : (selectedEmployee ? 6 : 7)} className="px-6 py-8 text-center text-gray-500">
                              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p>
                                {selectedEmployee
                                  ? `No tour plans found for ${selectedEmployee.name}`
                                  : 'No tour plans found for the selected filters.'
                                }
                              </p>
                              <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                            </td>
                          </tr>
                        ) : (
                          filteredTourPlans.map((plan) => (
                            <tr key={plan.id} className="hover:bg-gray-50">
                              {isSelectMode && (
                                <td className="px-6 py-4">
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.includes(plan.id)}
                                    onChange={(e) => handleItemSelect(plan.id, e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="flex items-center gap-2">
                                  {onViewTourPlan && (
                                    <button
                                      onClick={() => onViewTourPlan(plan)}
                                      className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                                      title="View"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  )}
                                  {onApproveTourPlan && (plan.status === 'pending' || plan.status === 'submitted') && (
                                    <button
                                      onClick={() => handleApproveTourPlan(plan)}
                                      className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                                      title="Approve"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                  )}
                                  {onRejectTourPlan && plan.status !== 'rejected' && (
                                    <button
                                      onClick={() => handleRejectTourPlan(plan)}
                                      className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                                      title="Reject"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                              {selectedEmployee && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {selectedEmployee.name}
                                  <div className="text-xs text-gray-500">{selectedEmployee.designation}</div>
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {fmtDateWithDay(plan.date)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {plan.locationName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {plan.deviation ? (
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Deviation
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {plan.approved_by || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(plan.status)}`}>
                                  {getStatusLabel(plan.status)}
                                </span>
                              </td>
                            </tr>
                          ))
                        )
                      ) : (
                        filteredLeaves.length === 0 ? (
                          <tr>
                            <td colSpan={isSelectMode ? (selectedEmployee ? 6 : 7) : (selectedEmployee ? 5 : 6)} className="px-6 py-8 text-center text-gray-500">
                              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p>
                                {selectedEmployee
                                  ? `No leaves found for ${selectedEmployee.name}`
                                  : 'No leaves found for the selected filters.'
                                }
                              </p>
                              <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                            </td>
                          </tr>
                        ) : (
                          filteredLeaves.map((leave) => (
                            <tr key={leave.id} className="hover:bg-gray-50">
                              {isSelectMode && (
                                <td className="px-6 py-4">
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.includes(leave.id)}
                                    onChange={(e) => handleItemSelect(leave.id, e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="flex items-center gap-2">
                                  {onViewLeave && (
                                    <button
                                      onClick={() => onViewLeave(leave)}
                                      className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                                      title="View"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  )}
                                  {(leave.status === 'pending' || leave.status === 'submitted') && (
                                    <>
                                      {onApproveLeave && (
                                        <button
                                          onClick={() => handleApproveLeave(leave)}
                                          className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                                          title="Approve"
                                        >
                                          <Check className="w-4 h-4" />
                                        </button>
                                      )}
                                      {onRejectLeave && (
                                        <button
                                          onClick={() => handleRejectLeave(leave)}
                                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                                          title="Reject"
                                        >
                                          <XCircle className="w-4 h-4" />
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                              {selectedEmployee && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {selectedEmployee?.name}
                                  <div className="text-xs text-gray-500">{selectedEmployee.designation}</div>
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {fmtDateWithDay(leave.date)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                  {leave.leaveType}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                {leave.reason || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(leave.status)}`}>
                                  {getStatusLabel(leave.status)}
                                </span>
                              </td>
                            </tr>
                          ))
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <p>
              Total: {activeTab === 'tour-plans' ? filteredTourPlans.length : filteredLeaves.length} items
              {selectedItems.length > 0 && ` • ${selectedItems.length} selected`}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Action Modal */}
      {showBulkActionModal.visible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                {showBulkActionModal.actionType === 'approve' ? (
                  <CheckCircle className="w-12 h-12 text-green-500" />
                ) : (
                  <XCircleFull className="w-12 h-12 text-red-500" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
                {showBulkActionModal.actionType === 'approve' ? 'Approve Selected Items' : 'Reject Selected Items'}
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to {showBulkActionModal.actionType} {selectedItems.length} item(s)?
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <XCircleFull className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                  <p className="text-yellow-800 text-sm">
                    <span className="font-semibold">Warning:</span> This action will {showBulkActionModal.actionType} all selected items at once. This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBulkActionModal({ visible: false, actionType: 'approve' })}
                  className="flex-1 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={executeBulkAction}
                  className={`flex-1 py-3 rounded-lg font-medium text-white ${showBulkActionModal.actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {showBulkActionModal.actionType === 'approve' ? 'Approve All' : 'Reject All'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproveTourPlanModal;